/**
 * `SyncTransportPort` implementation: fetch-based HTTP transport to the
 * Worker (client half of ADR-0004; wire shapes frozen in worker-api.ts).
 *
 * Responsibilities specific to this port (ports.ts doc comment): detect
 * Access-session expiry (a redirect or HTML response instead of JSON) and
 * throw a distinguishable `ReauthRequiredError` rather than a retryable one
 * -- the queue driver (`outbound-queue.ts`) is the only caller and branches
 * on this error type per ADR-0004's error/retry table.
 */

import {
  API_ROUTES,
  type ExportFile,
  type CompassEvent,
  type PullResponse,
  type PushResponse,
  type SyncCursor,
  type SyncTransportPort,
} from '../../contracts';

/** Access session expired -- stop the queue, never retry-spin (ADR-0004). */
export class ReauthRequiredError extends Error {
  constructor(message = 'Access session expired; re-authentication required') {
    super(message);
    this.name = 'ReauthRequiredError';
  }
}

/** A batch was rejected by the Worker's shape validation (ADR-0005). */
export class InvalidRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidRequestError';
  }
}

/** Anything else non-2xx: network failure, 5xx, 429 -- caller retries with backoff. */
export class RetryableSyncError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RetryableSyncError';
  }
}

export interface HttpSyncTransportOptions {
  /** Origin-relative or absolute base URL; defaults to same-origin ('' ). */
  readonly baseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

export function createHttpSyncTransport(
  options: HttpSyncTransportOptions = {},
): SyncTransportPort {
  const baseUrl = options.baseUrl ?? '';
  const fetchImpl = options.fetchImpl ?? fetch;

  async function push(events: readonly CompassEvent[]): Promise<PushResponse> {
    const response = await requestJson('POST', API_ROUTES.push, { events });
    return response as PushResponse;
  }

  async function pull(cursor: SyncCursor, limit: number): Promise<PullResponse> {
    const url = `${API_ROUTES.pull}?cursor=${encodeURIComponent(String(cursor))}&limit=${encodeURIComponent(String(limit))}`;
    const response = await requestJson('GET', url);
    return response as PullResponse;
  }

  async function fetchExport(): Promise<ExportFile> {
    const response = await requestJson('GET', API_ROUTES.export);
    return response as ExportFile;
  }

  async function requestJson(
    method: 'GET' | 'POST',
    path: string,
    body?: unknown,
  ): Promise<unknown> {
    let response: Response;
    try {
      response = await fetchImpl(`${baseUrl}${path}`, {
        method,
        // Per worker-api.ts: Access-session expiry may surface as a
        // redirect to the IdP rather than a JSON response. `redirect:
        // 'manual'` lets us detect that (response.type === 'opaqueredirect'
        // / status 0) instead of silently following it into an HTML page.
        redirect: 'manual',
        headers: body ? { 'content-type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'include',
      });
    } catch (error) {
      throw new RetryableSyncError(
        `network error calling ${path}: ${error instanceof Error ? error.message : String(error)}`,
      );
    }

    if (isRedirectLikeAuthFailure(response)) {
      throw new ReauthRequiredError();
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      // An HTML response where JSON was expected is the other documented
      // Access-expiry shape (worker-api.ts).
      throw new ReauthRequiredError();
    }

    if (response.status === 401 || response.status === 403) {
      throw new ReauthRequiredError();
    }

    if (response.status === 400) {
      const errorBody = await safeJson(response);
      throw new InvalidRequestError(
        (errorBody as { error?: { message?: string } } | null)?.error?.message ??
          'invalid request',
      );
    }

    if (!response.ok) {
      throw new RetryableSyncError(`request to ${path} failed with status ${response.status}`);
    }

    return response.json();
  }

  return { push, pull, fetchExport };
}

function isRedirectLikeAuthFailure(response: Response): boolean {
  // `redirect: 'manual'` makes a same-origin redirect resolve as an
  // "opaqueredirect" response (type is 'opaqueredirect', status is
  // typically 0) rather than being followed -- exactly the Access
  // login-page redirect shape worker-api.ts documents.
  return response.type === 'opaqueredirect' || response.status === 0;
}

async function safeJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
